import React from "react"
import { fetchOrderRefundDetails } from "@/services/order-refund-details"
import type { OrderRefundDetailsUI } from "@/domain/order/order-refund-details"

export type OrderRefundDetailsState =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "loaded"; details: OrderRefundDetailsUI }
  | { phase: "error"; message: string }

/**
 * On-demand refund details for one order. Nothing is fetched until {@link load} is called; a load
 * in flight or already completed is never repeated (only a failed one can be retried). A change of
 * order resets the state, and an unmounted or superseded request is aborted.
 */
export function useOrderRefundDetails(orderId: string | undefined) {
  const [state, setState] = React.useState<OrderRefundDetailsState>({ phase: "idle" })
  const inFlight = React.useRef<AbortController | null>(null)
  const phaseRef = React.useRef<OrderRefundDetailsState["phase"]>("idle")

  const update = React.useCallback((next: OrderRefundDetailsState) => {
    phaseRef.current = next.phase
    setState(next)
  }, [])

  React.useEffect(() => {
    update({ phase: "idle" })
    return () => {
      inFlight.current?.abort()
      inFlight.current = null
    }
  }, [orderId, update])

  const load = React.useCallback(async () => {
    const id = orderId?.trim()
    if (!id) return
    if (phaseRef.current === "loading" || phaseRef.current === "loaded") return

    const controller = new AbortController()
    inFlight.current = controller
    update({ phase: "loading" })
    try {
      const details = await fetchOrderRefundDetails(id, controller.signal)
      if (controller.signal.aborted) return
      update({ phase: "loaded", details })
    } catch (error) {
      if (controller.signal.aborted) return
      update({
        phase: "error",
        message: error instanceof Error && error.message ? error.message : "Unable to load refund details. Try again.",
      })
    } finally {
      if (inFlight.current === controller) inFlight.current = null
    }
  }, [orderId, update])

  return { state, load }
}
